import * as Highcharts from 'highcharts';
import HighchartsReact, { type HighchartsReactRefObject } from 'highcharts-react-official';
import React, { useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';

import SkeletonBlock from '@components/common/Skeleton/SkeletonBlock';
import Select from '@components/uikit/Select/Select';
import Switch from '@components/uikit/Switch/Switch';
import Tabs from '@components/uikit/Tabs/Tabs';

import useTranslation from '@hooks/useTranslation';

import {
  selectChartType,
  selectCostSavingsIncludeUnverifiedSavings,
  selectCostSavingsYearFilter,
  useCostSavingsActions,
} from '@pages/CostSavingsDetails/store/useCostSavingsStore';

import { useAssetTreeQuery } from '@services/useAssetTreeQuery';

import { selectAppLocationSelectValue } from '@store/useAppStore';

import { useGetTotalCostSavingOptions } from '../../hooks/useGetTotalCostSavingOptions';
import { useTotalCostSavingsLifetimeQuery } from '../../services/useTotalCostSavingsLifetime';
import { useTotalCostSavingsQuery } from '../../services/useTotalCostSavingsQuery';
import NoData from '../NoData/NoData';
import StatisticWrapper from '../StatisticWrapper/StatisticWrapper';
import style from './style.module.scss';

const TotalCostSaving = () => {
  const { customerId } = useParams();
  const { t } = useTranslation();
  const { setYearFilter, setChartType, setIncludeUnverifiedSavings } = useCostSavingsActions();

  const chartRef = useRef<HighchartsReactRefObject>(null);

  const locationSelectValue = selectAppLocationSelectValue();
  const yearFilter = selectCostSavingsYearFilter();
  const chartType = selectChartType();
  const includeUnverifiedSavings = selectCostSavingsIncludeUnverifiedSavings();

  const { data: useTotalCostSavingsData, isLoading: useTotalCostSavingsIsLoading } = useTotalCostSavingsQuery(
    {
      locationIds:
        locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
      year: yearFilter.value,
    },
  );

  const { data: useTotalCostSavingsLifetimeData, isLoading: useTotalCostSavingsLifetimeIsLoading } =
    useTotalCostSavingsLifetimeQuery({
      locationIds:
        locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
    });

  const { data: assetTreeData } = useAssetTreeQuery();

  const isSelectedAllFacility = locationSelectValue.id === 'all';

  const currentCurrency = isSelectedAllFacility
    ? assetTreeData?.customerCurrency
    : locationSelectValue.currency;

  const { costSavingsOptions, downtimeAvoidedOptions } =
    useGetTotalCostSavingOptions(useTotalCostSavingsData);

  const hasChartData =
    !!useTotalCostSavingsData?.byMonthCostSavings?.length &&
    !!useTotalCostSavingsData?.byMonthPotentialCostSavings?.length &&
    !!useTotalCostSavingsData?.byMonthDowntimeAvoided?.length &&
    !!useTotalCostSavingsData?.byMonthPotentialDowntimeAvoided?.length;

  const handleToggleChange = () => {
    setIncludeUnverifiedSavings(!includeUnverifiedSavings);

    const chart = chartRef.current?.chart;
    if (!chart) return;

    chart.series.forEach((series) => {
      if (series.name === 'Unverified') {
        series.setVisible(!includeUnverifiedSavings, false);
      }
    });

    chart.redraw();
  };

  const TOTAL_COST_SAVING_TOOLTIP_ID = 'total-cost-saving-tooltip';

  return (
    <StatisticWrapper
      title={t('Total Cost Savings')}
      rightElement={
        <div className={style.selectWrapper}>
          {useTotalCostSavingsLifetimeIsLoading ? (
            <SkeletonBlock className={style.skeletonTab} />
          ) : (
            <Select
              value={yearFilter}
              onChange={setYearFilter}
              options={useTotalCostSavingsLifetimeData?.years || []}
              itemKeys={{ valueFieldKey: 'value', labelFieldKey: 'label' }}
              type="woHelperText"
            />
          )}
        </div>
      }
      link={`/customer/${customerId}/executive-summary/cost-savings-details`}
    >
      <>
        <div className={style.savings}>
          <div className={style.savingsBlock}>
            <div className={style.savingsCount}>
              {useTotalCostSavingsIsLoading ? (
                <SkeletonBlock className={style.skeletonText} />
              ) : (
                `${currentCurrency.symbol} ${useTotalCostSavingsData?.ytdSavings || 0}`
              )}
            </div>
            <div className={style.savingsCountText}>{t('Verified Cost Savings')}</div>
          </div>
          <div className={style.savingsBlock}>
            <div className={style.savingsCount}>
              {useTotalCostSavingsIsLoading ? (
                <SkeletonBlock className={style.skeletonText} />
              ) : (
                `${currentCurrency.symbol} ${useTotalCostSavingsData?.potentialCostSavings || 0}`
              )}
            </div>
            <div className={style.savingsCountText}>
              <span
                data-tooltip-id={TOTAL_COST_SAVING_TOOLTIP_ID}
                data-tooltip-content={t(
                  'Savings from completion reports that are not yet verified by Waites',
                )}
              >
                {t('Unverified Cost Savings')}
              </span>
            </div>
          </div>
          <div className={style.savingsBlock}>
            <div className={style.savingsCount}>
              {useTotalCostSavingsLifetimeIsLoading ? (
                <SkeletonBlock className={style.skeletonText} />
              ) : (
                `${currentCurrency.symbol} ${
                  (includeUnverifiedSavings
                    ? useTotalCostSavingsLifetimeData?.totalSavings
                    : useTotalCostSavingsLifetimeData?.verifiedSavings) || 0
                }`
              )}
            </div>
            <div className={style.savingsCountText}>{t('Lifetime Savings')}</div>
          </div>
          <div className={style.savingsBlock}>
            <div className={style.savingsCount}>
              {useTotalCostSavingsIsLoading ? (
                <SkeletonBlock className={style.skeletonText} />
              ) : (
                `${
                  (includeUnverifiedSavings
                    ? useTotalCostSavingsData?.totalDowntimeAvoided
                    : useTotalCostSavingsData?.verifiedDowntimeAvoided) || 0
                } ${t('hrs')}`
              )}
            </div>
            <div className={style.savingsCountText}>{t('Unplanned Downtime Avoided')}</div>
          </div>
        </div>

        <div className={style.tabsBlock}>
          {useTotalCostSavingsIsLoading ? (
            <SkeletonBlock className={style.skeletonTab} />
          ) : (
            <>
              <div className={style.tabContainer}>
                <Tabs
                  id="total-cost-saving-chart-type"
                  value={chartType}
                  tabsItems={[
                    { value: 'costSavings', label: `${t('Cost Savings')} (${currentCurrency.symbol})` },
                    {
                      value: 'unplannedDowntimeAvoided',
                      label: `${t('Unplanned Downtime Avoided')} (${t('hrs')})`,
                    },
                  ]}
                  type="woHelperText"
                  onChange={setChartType}
                />
              </div>
              <div
                data-tooltip-id={TOTAL_COST_SAVING_TOOLTIP_ID}
                data-tooltip-content={t('Include unverified savings from open action items')}
              >
                <Switch
                  value={includeUnverifiedSavings}
                  onChange={handleToggleChange}
                  label={t('Include unverified')}
                  type="woHelperText"
                />
              </div>
            </>
          )}
        </div>
        {useTotalCostSavingsIsLoading && <SkeletonBlock className={style.skeleton} />}
        {!useTotalCostSavingsIsLoading && hasChartData && (
          <HighchartsReact
            highcharts={Highcharts}
            options={chartType === 'costSavings' ? costSavingsOptions : downtimeAvoidedOptions}
            containerProps={{ className: 'printHighchartsChart' }}
            ref={chartRef}
          />
        )}
        {!useTotalCostSavingsIsLoading && !hasChartData && <NoData />}
        <Tooltip id={TOTAL_COST_SAVING_TOOLTIP_ID} place="bottom" />
      </>
    </StatisticWrapper>
  );
};

export default TotalCostSaving;
